import React, { useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faDownload } from "@fortawesome/free-solid-svg-icons";
import "../../style.scss";
import "./style.scss";
import { useAppContext } from "../../../../../../AppContext";

function Promotions(props) {
  const [braKey, setBraKey] = useState("");
  const [mfzId, setMFZId] = useState("");
  const [isoCountryCode, setIsoCountryCode] = useState("");
  const [promotionsData, setPromotionsData] = useState([]);
  const [rawPromotions, setRawPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const { chimeraserv } = useAppContext();

  // Generate CSV from the fetched data
  const generateCSV = (promotions) => {
    if (!promotions || promotions.length === 0) {
      return "";
    }
    const headers = [
      "MFZ_ID",
      "iso_country_code",
      "promotion_ID",
      "promotion_name",
      "promotion_type_ID",
      "start_date",
      "end_date",
    ];
    const rows = promotions.map((promo) => [
      promo.MFZ_ID || "",
      promo.iso_country_code || "",
      promo.promotion_ID || "",
      promo.promotion_name || "",
      promo.promotion_type_ID || "",
      promo.start_date || "",
      promo.end_date || "",
    ]);
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell);
            return cellStr.includes(",") || cellStr.includes('"')
              ? `"${cellStr.replace(/"/g, '""')}"`
              : cellStr;
          })
          .join(",")
      ),
    ].join("\n");
    return csvContent;
  };

  // Fetch promotions list
  const handleFetchPromotions = async (e) => {
    e.preventDefault();
    setError(null);
    setPromotionsData([]);
    setRawPromotions([]);
    setDataFetched(false);
    if (!braKey.trim()) {
      setError("Please enter a Brand Key");
      return;
    }
    setLoading(true);
    try {
      const token =
        props.user?.data?.usrKey ||
        "eyJ1c2VybmFtZSI6ImNoc0FkbWluIiwicGFzc3dvcmQiOiIhbnNpdGVzMjAyMSJ9";

      let url = `${chimeraserv.urlInsightBackendReading}/api/promotions/${braKey}/${chimeraserv.mfzId}/`;

      const response = await axios.get(url, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });
      if (response.data?.data) {
        const promotions = Array.isArray(response.data.data)
          ? response.data.data
          : [response.data.data];
        setRawPromotions(promotions);
        setPromotionsData(promotions);
        setDataFetched(true);
      } else {
        setError("No promotions data found in response");
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Error fetching promotions"
      );
      console.error("Promotions fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Download CSV file
  const handleDownloadCSV = () => {
    if (rawPromotions.length === 0) {
      setError("No data available for CSV export");
      return;
    }
    try {
      const csvContent = generateCSV(rawPromotions);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0].replace(/-/g, "");
      link.setAttribute(
        "download",
        `${chimeraserv.mfzId}_promotions_${formattedDate}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError("Error generating CSV: " + err.message);
    }
  };

  // Push promotions to S3
  const handlePushToS3 = async () => {
    if (rawPromotions.length === 0) {
      setError("No data available to push to S3");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const token =
        props.user?.data?.usrKey ||
        "eyJ1c2VybmFtZSI6ImNoc0FkbWluIiwicGFzc3dvcmQiOiIhbnNpdGVzMjAyMSJ9";
      const response = await axios.post(
        `${chimeraserv.urlAwsPusher}/upload/promotions`,
        { data: rawPromotions },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      if (response.data && response.data.success) {
        setError(null);
        alert(
          "Successfully pushed promotions to S3.\nS3 URL: " + response.data.url
        );
      } else {
        setError(response.data.error || "Failed to push to S3");
      }
    } catch (error) {
      setError(
        error.response?.data?.error ||
          error.message ||
          "Failed to push promotions to S3"
      );
    } finally {
      setLoading(false);
    }
  };

  // Get table columns
  const getTableColumns = () => {
    if (promotionsData.length === 0) return [];
    return Object.keys(promotionsData[0]);
  };

  // Format column name from snake_case to Title Case
  const formatColumnName = (columnName) => {
    return columnName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Format cell value for display
  const formatCellValue = (value) => {
    if (typeof value === "number") {
      return value;
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return value || "-";
  };

  const columns = getTableColumns();

  return (
    <div className="products-container">
      <div className="products-header">
        <h2>Promotions List</h2>
        <p>Fetch and download promotions data from the system</p>
      </div>
      <form onSubmit={handleFetchPromotions} className="products-form">
        <div className="form-group">
          <label htmlFor="braKey">Brand Key *</label>
          <input
            type="text"
            id="braKey"
            value={braKey}
            onChange={(e) => setBraKey(e.target.value)}
            placeholder="Enter Brand Key (e.g., BRA001)"
            required
          />
        </div>

        <button type="submit" disabled={loading} className="btn-fetch">
          {loading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Fetching...
            </>
          ) : (
            "Fetch Promotions"
          )}
        </button>
      </form>
      {error && <div className="alert alert-error">{error}</div>}
      {dataFetched && promotionsData.length === 0 && (
        <div className="alert alert-warning">No promotions data found</div>
      )}
      {dataFetched && promotionsData.length > 0 && (
        <>
          <div className="results-header">
            <h3>Promotions Data ({promotionsData.length} records)</h3>
            <button
              onClick={handleDownloadCSV}
              disabled={loading}
              className="btn-download"
            >
              <FontAwesomeIcon icon={faDownload} /> Download CSV
            </button>
            <button
              onClick={handlePushToS3}
              disabled={loading || rawPromotions.length === 0}
              className="btn-download"
              style={{ marginLeft: "10px" }}
            >
              <FontAwesomeIcon icon={faDownload} /> Push
            </button>
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col}>{formatColumnName(col)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {promotionsData.map((row, idx) => (
                  <tr key={idx}>
                    {columns.map((col) => (
                      <td key={`${idx}-${col}`}>{formatCellValue(row[col])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default Promotions;
